/**
 * Admin Organizations List
 * Platform monitoring dashboard to view and manage existing organizations
 * This is a read-only overview - organizations are created during user signup
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { adminApiGet } from '../../utils/adminApi';
import toast from '../../utils/toast';

import {
  ArrowsClockwise,
  Buildings,
  CheckCircle,
  CircleNotch,
  Clock,
  Eye,
  MagnifyingGlass,
  Robot,
  UsersThree,
  XCircle
} from '@phosphor-icons/react';
const OrganizationsList = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_desc');

  useEffect(() => {
    fetchOrganizations();
  }, [tierFilter, sortBy]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApiGet('/api/admin/organizations');
      setOrganizations(data.organizations || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setError(error.message || 'Failed to load organizations');
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const filteredOrganizations = organizations
    .filter(org => {
      const matchesSearch = org.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          org.organization_id?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesTier = tierFilter === 'all' || org.tier === tierFilter;
      return matchesSearch && matchesTier;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'created_desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'created_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'name_asc':
          return (a.name || '').localeCompare(b.name || '');
        case 'name_desc':
          return (b.name || '').localeCompare(a.name || '');
        default:
          return 0;
      }
    });

  const getTierBadge = (tier) => {
    const colors = {
      basic: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      pro: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'partner-pro': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      partner_pro: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    };
    return colors[tier?.toLowerCase()] || colors.basic;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-white/10 rounded animate-pulse w-64"></div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-24 bg-white/10 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  // Error state with retry option
  if (error && organizations.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Organizations</h1>
          <p className="text-gray-400">Platform organization monitoring</p>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-8 text-center">
          <XCircle size={48} className="text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Failed to Load Organizations</h3>
          <p className="text-gray-400 mb-4">{error}</p>
          <button
            onClick={fetchOrganizations}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition-colors mx-auto"
          >
            <ArrowsClockwise size={18} />
            <span>Try Again</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Organizations</h1>
          <p className="text-gray-400">
            {filteredOrganizations.length} organization{filteredOrganizations.length !== 1 ? 's' : ''} on the platform
          </p>
        </div>
        <button
          onClick={fetchOrganizations}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
        >
          <ArrowsClockwise size={18} />
          <span className="font-medium">ArrowsClockwise</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* MagnifyingGlass */}
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              placeholder="Search organizations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
            />
          </div>

          {/* Tier Filter */}
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="all">All Tiers</option>
            <option value="basic">Basic</option>
            <option value="pro">Pro</option>
            <option value="partner-pro">Partner Pro</option>
          </select>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
          >
            <option value="created_desc">Newest First</option>
            <option value="created_asc">Oldest First</option>
            <option value="name_asc">Name (A-Z)</option>
            <option value="name_desc">Name (Z-A)</option>
          </select>
        </div>
      </div>

      {/* Organizations List */}
      <div className="space-y-4">
        {filteredOrganizations.map((org, index) => (
          <motion.div
            key={org.organization_id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            onClick={() => navigate(`/admin/organizations/${org.organization_id}`)}
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-[#6366F1]/30 transition-all cursor-pointer group"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center flex-shrink-0">
                  <Buildings size={24} className="text-white" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white group-hover:text-[#6366F1] transition-colors">
                      {org.name || 'Unnamed Organization'}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getTierBadge(org.tier)}`}>
                      {org.tier || 'basic'}
                    </span>
                    {/* Status indicator */}
                    <span className={`flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                      org.status === 'suspended'
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                        : 'bg-green-500/20 text-green-400 border border-green-500/30'
                    }`}>
                      {org.status === 'suspended' ? <XCircle size={12} /> : <CheckCircle size={12} />}
                      {org.status === 'suspended' ? 'Suspended' : 'Active'}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-400 mb-3">
                    ID: {org.organization_id}
                  </p>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="flex items-center gap-2">
                      <UsersThree size={16} className="text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-500">UsersThree</p>
                        <p className="text-sm font-medium text-white">{org.user_count || 0}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Robot size={16} className="text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-500">Agents</p>
                        <p className="text-sm font-medium text-white">{org.agent_count || 0}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-500">Created</p>
                        <p className="text-sm font-medium text-white">
                          {org.created_at ? new Date(org.created_at).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/organizations/${org.organization_id}`);
                }}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
              >
                <Eye size={18} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Empty State */}
      {filteredOrganizations.length === 0 && !loading && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-12 text-center">
          <Buildings size={48} className="text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No Organizations Found</h3>
          <p className="text-gray-400 mb-4">
            {searchQuery || tierFilter !== 'all'
              ? 'No organizations match your current filters. Try adjusting your search criteria.'
              : 'No organizations have been created on the platform yet. Organizations are created when users sign up.'}
          </p>
          {(searchQuery || tierFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setTierFilter('all');
              }}
              className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20 transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default OrganizationsList;
